/**
 * Mooni-style neon outline 3D icons.
 * Each icon: ~64px viewBox, outlined strokes in pink gradient with soft glow filter.
 */
import React from "react";

const Defs = ({ id }) => (
  <defs>
    <linearGradient id={`${id}-stroke`} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#EADFFE" />
      <stop offset="40%" stopColor="#A16BF7" />
      <stop offset="100%" stopColor="#C12862" />
    </linearGradient>
    <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="2.2" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
);

const wrap = (children, id) => (
  <svg viewBox="0 0 64 64" width="40" height="40" fill="none" stroke={`url(#${id}-stroke)`} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" filter={`url(#${id}-glow)`}>
    <Defs id={id} />
    {children}
  </svg>
);

// Isometric cylinder (uptime)
export const NeonCylinder = () => wrap(
  <g>
    <ellipse cx="32" cy="20" rx="16" ry="6" />
    <ellipse cx="32" cy="20" rx="10" ry="3.8" opacity="0.6" />
    <path d="M16 20 L16 44" />
    <path d="M48 20 L48 44" />
    <path d="M16 44 A 16 6 0 0 0 48 44" />
    <path d="M22 28 A 16 6 0 0 0 42 28" opacity="0.45" />
    <path d="M22 36 A 16 6 0 0 0 42 36" opacity="0.45" />
  </g>,
  "cyl"
);

// Stacked cubes (lines of code)
export const NeonCubes = () => wrap(
  <g>
    {/* bottom cube */}
    <path d="M14 42 L32 50 L50 42 L32 34 Z" />
    <path d="M14 42 L14 30 L32 22 L50 30 L50 42" />
    <path d="M32 22 L32 34" opacity="0.5" />
    {/* top cube */}
    <path d="M22 24 L32 28 L42 24 L32 20 Z" />
    <path d="M22 24 L22 18 L32 14 L42 18 L42 24" />
    <path d="M32 14 L32 20" opacity="0.5" />
  </g>,
  "cube"
);

// Layered sphere (community)
export const NeonSphere = () => wrap(
  <g>
    <circle cx="32" cy="32" r="18" />
    <ellipse cx="32" cy="32" rx="18" ry="6" />
    <ellipse cx="32" cy="32" rx="18" ry="11" opacity="0.55" />
    <path d="M14 32 A 18 18 0 0 0 50 32" opacity="0.7" />
    <circle cx="32" cy="14" r="1.4" fill="#EADFFE" stroke="none" />
    <circle cx="32" cy="50" r="1.4" fill="#EADFFE" stroke="none" />
  </g>,
  "sphere"
);

// Twisted knot (issues resolved)
export const NeonKnot = () => wrap(
  <g>
    <path d="M20 22 C 14 28, 14 38, 22 42 C 30 46, 38 38, 32 30 C 26 22, 38 18, 44 24 C 50 30, 50 40, 42 44" />
    <path d="M20 22 C 26 18, 36 18, 42 24" opacity="0.55" />
    <circle cx="22" cy="22" r="1.6" fill="#EADFFE" stroke="none" />
    <circle cx="42" cy="44" r="1.6" fill="#EADFFE" stroke="none" />
  </g>,
  "knot"
);

// Microphone neon (for feature)
export const NeonMic = () => wrap(
  <g>
    <rect x="26" y="14" width="12" height="22" rx="6" />
    <path d="M20 30 C 20 38, 26 44, 32 44 C 38 44, 44 38, 44 30" />
    <path d="M32 44 L32 52" />
    <path d="M24 52 L40 52" />
  </g>,
  "mic"
);

// Heart neon
export const NeonHeart = () => wrap(
  <g>
    <path d="M32 50 C 16 40, 10 30, 14 22 C 18 14, 28 14, 32 22 C 36 14, 46 14, 50 22 C 54 30, 48 40, 32 50 Z" />
    <path d="M22 26 A 4 4 0 0 1 30 26" opacity="0.55" />
  </g>,
  "heart"
);

// Spark/sparkle neon
export const NeonSparkle = () => wrap(
  <g>
    <path d="M32 12 L36 28 L52 32 L36 36 L32 52 L28 36 L12 32 L28 28 Z" />
    <path d="M44 18 L46 24 L52 26 L46 28 L44 34 L42 28 L36 26 L42 24 Z" opacity="0.6" />
  </g>,
  "spark"
);

// Trending up
export const NeonTrend = () => wrap(
  <g>
    <path d="M12 46 L24 34 L32 40 L52 18" />
    <path d="M40 18 L52 18 L52 30" />
    <circle cx="24" cy="34" r="1.6" fill="#EADFFE" stroke="none" />
    <circle cx="32" cy="40" r="1.6" fill="#EADFFE" stroke="none" />
  </g>,
  "trend"
);

// Book/edu
export const NeonBook = () => wrap(
  <g>
    <path d="M14 18 C 14 14, 30 14, 32 20 C 34 14, 50 14, 50 18 L 50 46 C 50 50, 34 50, 32 46 C 30 50, 14 50, 14 46 Z" />
    <path d="M32 20 L32 46" opacity="0.55" />
    <path d="M18 24 L28 24" opacity="0.5" />
    <path d="M18 30 L28 30" opacity="0.5" />
  </g>,
  "book"
);

// Users
export const NeonUsers = () => wrap(
  <g>
    <circle cx="24" cy="22" r="6" />
    <path d="M12 46 C 12 38, 18 34, 24 34 C 30 34, 36 38, 36 46" />
    <circle cx="42" cy="24" r="5" />
    <path d="M36 44 C 38 38, 44 36, 50 38 C 54 40, 54 46, 52 48" />
  </g>,
  "users"
);

export default {
  NeonCylinder, NeonCubes, NeonSphere, NeonKnot,
  NeonMic, NeonHeart, NeonSparkle, NeonTrend, NeonBook, NeonUsers,
};
